/**
 * EmployeeOverride.jsx — Personel Mesai İstisna Yönetimi
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Search, X, Loader2, UserX } from 'lucide-react';
import toast from 'react-hot-toast';
import { workScheduleApi } from '../../../api/axios';
import EmployeeAvatar from '../../../components/common/EmployeeAvatar';

const EmployeeOverride = ({
  employees = [],
  schedules = [],
  overrides = [],
  loading,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedDept, setSelectedDept] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignScheduleId, setAssignScheduleId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Override edilmiş personel ID'leri
  const overrideEmployeeIds = new Set((overrides || []).map((o) => o.employee_id));

  // Departman listesi (unique)
  const deptList = [...new Map((employees || []).map((e) => [e.department_id, e])).values()]
    .filter((e) => e.department_name)
    .map((e) => ({ id: e.department_id, name: e.department_name }));

  // Filtreleme
  const filtered = (employees || []).filter((e) => {
    const matchQuery = !query || `${e.first_name} ${e.last_name}`.toLowerCase().includes(query.toLowerCase());
    const matchDept = selectedDept === 'ALL' || String(e.department_id) === selectedDept;
    return matchQuery && matchDept;
  });

  const toggleEmployee = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (!selectedIds.length || !assignScheduleId) {
      toast.error(t('override_select_both', 'Personel ve mesai planı seçin.'));
      return;
    }
    setSaving(true);
    try {
      await workScheduleApi.assignEmployeesBulk({
        employee_ids: selectedIds,
        work_schedule_id: parseInt(assignScheduleId, 10),
        effective_from: effectiveFrom,
        reason,
      });
      toast.success(t('override_assigned', 'Personel istisnaları atandı.'));
      setSelectedIds([]);
      setAssignScheduleId('');
      onRefresh?.();
    } catch {
      toast.error(t('err_override_assign', 'İstisna atanamadı.'));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(t('override_clear_confirm', 'Seçili personelin istisnalarını kaldırmak istediğinize emin misiniz?'))) return;
    setSaving(true);
    try {
      await workScheduleApi.clearEmployeeOverrides(selectedIds);
      toast.success(t('override_cleared', 'İstisnalar kaldırıldı.'));
      setSelectedIds([]);
      onRefresh?.();
    } catch {
      toast.error(t('err_override_clear', 'İstisnalar kaldırılamadı.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">
          {t('override_kicker', 'Personel İstisnası')}
        </p>
        <h3 className="mt-1 text-lg font-black text-slate-900">
          {t('override_title', 'Personel Bazlı Mesai İstisnası')}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          {t('override_desc', 'Belirli personellere departman mesai planından farklı bir plan ata.')}
        </p>
      </div>

      {/* Arama + Departman filtresi */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('override_search', 'Personel ara...')}
            className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-xs font-medium"
          />
        </div>
        <select
          value={selectedDept}
          onChange={(e) => setSelectedDept(e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold min-w-[150px]"
        >
          <option value="ALL">{t('override_all_departments', 'Tüm Departmanlar')}</option>
          {deptList.map((d) => (
            <option key={d.id} value={String(d.id)}>{d.name}</option>
          ))}
        </select>
      </div>

      {/* Personel listesi */}
      <div className="max-h-[320px] overflow-y-auto space-y-1.5">
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">{t('override_empty', 'Personel bulunamadı.')}</p>
        ) : (
          filtered.map((emp) => {
            const isOverride = overrideEmployeeIds.has(emp.id);
            const isSelected = selectedIds.includes(emp.id);
            return (
              <button
                key={emp.id}
                type="button"
                onClick={() => toggleEmployee(emp.id)}
                className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-cyan-200 bg-cyan-50'
                    : 'border-slate-100 hover:bg-slate-50'
                }`}
              >
                <EmployeeAvatar firstName={emp.first_name} lastName={emp.last_name} size={32} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-800 truncate">
                    {emp.first_name} {emp.last_name}
                  </p>
                  <p className="text-[10px] text-slate-400">{emp.department_name || '-'}</p>
                </div>
                {isOverride && (
                  <span className="text-[9px] font-black uppercase text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">
                    {t('override_badge', 'İSTİSNA')}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Atama formu */}
      {selectedIds.length > 0 && (
        <div className="rounded-2xl bg-slate-50 p-4 space-y-3">
          <p className="text-xs font-bold text-slate-600">
            {selectedIds.length} {t('override_selected', 'personel seçildi')}
          </p>
          <div className="flex flex-wrap gap-3">
            <select
              value={assignScheduleId}
              onChange={(e) => setAssignScheduleId(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold min-w-[200px]"
            >
              <option value="">{t('override_select_schedule', 'Mesai planı seçin...')}</option>
              {(schedules || []).map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium"
            />
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('override_reason', 'Sebep (opsiyonel)')}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium min-w-[150px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAssign}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-xl bg-cyan-600 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t('override_assign_btn', 'Ata')}
            </button>
            <button
              onClick={handleClear}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-xl bg-rose-50 px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-100 disabled:opacity-50"
            >
              <UserX size={14} />
              {t('override_clear_btn', 'İstisnaları Kaldır')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeOverride;